/**
 * Utility functions for bulk actions
 */

/**
 * Maximum number of records that can be selected for bulk actions
 */
export const BULK_SELECTION_LIMIT = 1000;

/**
 * Entity type configurations
 */
export const ENTITY_CONFIGS = {
  patient: {
    // Order matches Figma design: Assignee, Facility, Physician, Status
    actions: ["assignees_set", "assignees_add", "assignees_remove", "facility", "physician", "status"]
  },
  enrollment: {
    // Order matches Figma design: Assignee first, then Status
    actions: ["assignees_set", "assignees_add", "assignees_remove", "status"]
  },
  claim: {
    actions: ["status"]
  },
  order: {
    actions: ["status"]
  }
};

/**
 * Action type configurations
 * Labels are translation keys - use with t() from useTranslation
 */
const ACTION_CONFIGS = {
  status: { labelKey: "bulk_actions.actions.status", icon: "status" },
  facility: { labelKey: "bulk_actions.actions.facility", icon: "facility" },
  physician: { labelKey: "bulk_actions.actions.physician", icon: "physician" },
  assignees_set: { labelKey: "bulk_actions.actions.assignee", icon: "assignee", mode: "set" },
  assignees_add: { labelKey: "bulk_actions.actions.assignee", icon: "assignee", mode: "add" },
  assignees_remove: { labelKey: "bulk_actions.actions.assignee", icon: "assignee", mode: "remove" }
};

/**
 * Get action display configuration
 * Use labelKey with t() from useTranslation: t(config.labelKey)
 * @param {string} action - Action type
 * @returns {Object} Action configuration with labelKey (translation key), icon, and optional mode
 */
export const getActionConfig = (action) => {
  return ACTION_CONFIGS[action] || { labelKey: `bulk_actions.actions.${action}`, icon: "default" };
};

/**
 * Format action type for display in CSV header
 * @param {string} actionType - Action type (status, assignees_set, etc.)
 * @param {Function} t - Translation function
 * @returns {string} Human-readable action name
 */
const formatActionType = (actionType, t) => {
  const actionMap = {
    status: t("bulk_actions.report.action_update_status"),
    facility: t("bulk_actions.report.action_update_facility"),
    physician: t("bulk_actions.report.action_update_physician"),
    assignees_set: t("bulk_actions.report.action_update_assignee_set"),
    assignees_add: t("bulk_actions.report.action_update_assignee_add"),
    assignees_remove: t("bulk_actions.report.action_update_assignee_remove")
  };
  return actionMap[actionType] || actionType;
};

/**
 * Format entity type for display in CSV header
 * @param {string} entityType - Entity type (patient, enrollment, etc.)
 * @param {Function} t - Translation function
 * @returns {string} Capitalized entity name
 */
const formatEntityType = (entityType, t) => {
  const entityMap = {
    patient: t("bulk_actions.report.entity_patient"),
    enrollment: t("bulk_actions.report.entity_enrollment"),
    claim: t("bulk_actions.report.entity_claim"),
    order: t("bulk_actions.report.entity_order")
  };
  return entityMap[entityType] || entityType.charAt(0).toUpperCase() + entityType.slice(1);
};

/**
 * Format timestamp for display in CSV header
 * @param {Date} timestamp - Date object
 * @returns {string} Formatted date string with timezone
 */
const formatTimestamp = (timestamp) => {
  return timestamp.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short"
  });
};

/**
 * Generate CSV content from bulk action results with metadata header
 * @param {Array} results - Array of result objects with recordId, success, and error
 * @param {Object} metadata - Optional metadata object with actionType, entityType, value, timestamp, summary, t
 * @param {Function} t - Translation function from useTranslation hook
 * @returns {string} CSV content
 */
export const generateCSVReport = (results, metadata = null, t) => {
  const headers = [
    t("bulk_actions.report.column_id"),
    t("bulk_actions.report.column_status"),
    t("bulk_actions.report.column_error")
  ];

  // Sort results: failures first, then by record ID
  const sortedResults = [...results].sort((a, b) => {
    // First sort by success status (false/failed first, true/success last)
    if (a.success !== b.success) {
      return a.success ? 1 : -1;
    }
    // Then sort by record ID ascending
    return a.recordId - b.recordId;
  });

  const rows = sortedResults.map((result) => [
    result.recordId,
    result.success ? t("bulk_actions.report.status_success") : t("bulk_actions.report.status_failed"),
    result.error || ""
  ]);

  let csvLines = [];

  // Add metadata header if provided
  if (metadata) {
    const { actionType, entityType, value, timestamp, summary } = metadata;

    // Wrap each metadata line in quotes to escape commas
    csvLines.push(`"# ${t("bulk_actions.report.header_title")}"`);
    csvLines.push(`"# ${formatTimestamp(timestamp)}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_entity")}: ${formatEntityType(entityType, t)}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_action")}: ${formatActionType(actionType, t)}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_new_value")}: ${value || t("bulk_actions.report.value_na")}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_total_records")}: ${summary.total}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_succeeded")}: ${summary.succeeded}"`);
    csvLines.push(`"# ${t("bulk_actions.report.header_failed")}: ${summary.failed}"`);
    csvLines.push(`"#"`);
  }

  // Add data rows
  csvLines.push(headers.join(","));
  // Escape double quotes by doubling them (CSV standard) e.g. "Hello, ""World"" -> "Hello, ""World"""
  csvLines.push(...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")));

  return csvLines.join("\n");
};

/**
 * Format value for display in CSV header
 * Converts IDs to human-readable names when options are available
 * @param {*} value - The value to format (could be ID, array of IDs, or string)
 * @param {string} actionType - Action type to determine how to format
 * @param {Array} options - Options array with {value, label} objects
 * @param {Function} t - Translation function from useTranslation hook
 * @returns {string} Formatted value for display
 */
export const formatValueForDisplay = (value, actionType, options = [], t) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return t("bulk_actions.report.value_na");
  }

  // For assignee actions with array of IDs or objects
  if (
    (actionType === "assignees_set" || actionType === "assignees_add" || actionType === "assignees_remove") &&
    Array.isArray(value)
  ) {
    if (value.length === 0) {
      return t("bulk_actions.report.value_none");
    }
    // Map objects with { id, type } to names
    const names = value
      .map((item) => {
        const option = options.find((opt) => opt.value === item.id);
        return option ? option.label : `ID: ${item.id}`;
      })
      .join("; ");
    return names;
  }

  // For single value actions (status, facility, physician)
  if (options.length > 0) {
    const option = options.find((opt) => opt.value === value);
    if (option) {
      return option.label;
    }
  }

  // Fallback to string representation
  return String(value);
};

/**
 * Download CSV report
 * @param {Array} results - Array of result objects
 * @param {Object} metadata - Metadata object with actionType, entityType, value, timestamp, summary
 * @param {Function} t - Translation function from useTranslation hook
 */
export const downloadCSVReport = (results, metadata, t) => {
  const csvContent = generateCSVReport(results, metadata, t);
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const fileName = `bulk_action_report_${metadata.entityType}_${timestamp}.csv`;

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Check if selection limit is reached
 * @param {number} currentCount - Current selection count
 * @returns {boolean} True if limit is reached
 */
export const isSelectionLimitReached = (currentCount) => {
  return currentCount >= BULK_SELECTION_LIMIT;
};

/**
 * Get available actions for an entity
 * @param {string} entity - Entity type
 * @returns {string[]} Array of available action types
 */
export const getAvailableActions = (entity) => {
  const config = ENTITY_CONFIGS[entity];
  return config ? config.actions : [];
};
